import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LoginPage }                from './pages/LoginPage';
import { DashboardPage }            from './pages/DashboardPage';
import { DemoControlsPage }         from './pages/DemoControlsPage';
import { ApplicationsPage }         from './pages/ApplicationsPage';
import { ApplicationDetailPage }    from './pages/ApplicationDetailPage';
import { AttendancePage }           from './pages/AttendancePage';
import { AlertsPage }               from './pages/AlertsPage';
import { GradesPage }               from './pages/GradesPage';
import { EducatorsPage }            from './pages/EducatorsPage';
import { EducatorDetailPage }       from './pages/EducatorDetailPage';
import { CPDPage }                  from './pages/CPDPage';
import { TPAPage }                  from './pages/TPAPage';
import { LibraryCataloguePage }     from './pages/LibraryCataloguePage';
import { LibraryLoansPage }         from './pages/LibraryLoansPage';
import { LibraryReservationsPage }  from './pages/LibraryReservationsPage';
import { TimetableVersionsPage }    from './pages/TimetableVersionsPage';
import { TimetableGridPage }        from './pages/TimetableGridPage';
import { SubstitutePage }           from './pages/SubstitutePage';
import { DPESchoolsPage }           from './pages/DPESchoolsPage';
import { DPEInspectionsPage }       from './pages/DPEInspectionsPage';
import { DPEApprovalsPage }         from './pages/DPEApprovalsPage';
import { DPEBulletinsPage }         from './pages/DPEBulletinsPage';
import { MoraSchoolsPage }          from './pages/MoraSchoolsPage';
import { ReportsPage }              from './pages/ReportsPage';
import { MOEDashboardPage }         from './pages/MOEDashboardPage';
import { EduBotPage }               from './pages/EduBotPage';
import { AppShell }                 from './components/AppShell';
import { getStoredUser, type AuthUser } from './lib/auth';
import { LangProvider }             from './lib/lang';
import { canAccessRoute }           from './lib/rbac';
import { UnauthorizedPage }         from './pages/UnauthorizedPage';

// Start MSW in demo/dev mode so every API call is intercepted with realistic
// seed data — no backend required.
async function enableMocking() {
  const isDemoMode = (import.meta as ImportMeta & { env: Record<string, string> }).env['VITE_DEMO_MODE'] === 'true';
  if (!isDemoMode) return;
  const { worker } = await import('./mocks/browser');
  return worker.start({
    onUnhandledRequest: 'bypass', // let non-API requests through
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

/** Route guard: redirects to /unauthorized if the current user lacks permission */
function ProtectedRoute({ user, children }: { user: AuthUser; children: React.ReactNode }) {
  const location = useLocation();
  if (!canAccessRoute(user, location.pathname)) {
    return <UnauthorizedPage user={user} />;
  }
  return <>{children}</>;
}

function App() {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());

  if (!user) {
    return <LoginPage onAuthenticated={setUser} />;
  }

  // Wrap every page element so the route guard fires inside BrowserRouter
  // (ProtectedRoute calls useLocation which requires a router context).
  const P = ({ children }: { children: React.ReactNode }) => (
    <ProtectedRoute user={user}>{children}</ProtectedRoute>
  );

  return (
    <BrowserRouter>
      <AppShell user={user} onLogout={() => setUser(null)}>
        <Routes>
          {/* Dashboard — all roles */}
          <Route path="/"                           element={<P><DashboardPage user={user} /></P>} />

          {/* Admin / school operations */}
          <Route path="/admin/demo-controls"        element={<P><DemoControlsPage user={user} /></P>} />
          <Route path="/admin/applications"         element={<P><ApplicationsPage user={user} /></P>} />
          <Route path="/admin/applications/:id"     element={<P><ApplicationDetailPage user={user} /></P>} />
          <Route path="/admin/grades"               element={<P><GradesPage user={user} /></P>} />
          <Route path="/admin/attendance"           element={<P><AttendancePage user={user} /></P>} />
          <Route path="/admin/attendance/alerts"    element={<P><AlertsPage user={user} /></P>} />

          {/* Educator management */}
          <Route path="/admin/educators"            element={<P><EducatorsPage user={user} /></P>} />
          <Route path="/admin/educators/:id"        element={<P><EducatorDetailPage user={user} /></P>} />
          <Route path="/admin/cpd"                  element={<P><CPDPage user={user} /></P>} />
          <Route path="/admin/tpa"                  element={<P><TPAPage user={user} /></P>} />

          {/* Library */}
          <Route path="/admin/library"              element={<P><LibraryCataloguePage user={user} /></P>} />
          <Route path="/admin/library/loans"        element={<P><LibraryLoansPage user={user} /></P>} />
          <Route path="/admin/library/reservations" element={<P><LibraryReservationsPage user={user} /></P>} />

          {/* Timetable */}
          <Route path="/admin/timetable"            element={<P><TimetableVersionsPage user={user} /></P>} />
          <Route path="/admin/timetable/substitute" element={<P><SubstitutePage user={user} /></P>} />
          <Route path="/admin/timetable/:versionId" element={<P><TimetableGridPage user={user} /></P>} />

          {/* DPE — dpe_officer, moe_admin, super_admin */}
          <Route path="/admin/dpe"                  element={<P><DPESchoolsPage user={user} /></P>} />
          <Route path="/admin/dpe/inspections"      element={<P><DPEInspectionsPage user={user} /></P>} />
          <Route path="/admin/dpe/approvals"        element={<P><DPEApprovalsPage user={user} /></P>} />
          <Route path="/admin/dpe/bulletins"        element={<P><DPEBulletinsPage user={user} /></P>} />

          {/* MORA — mora_officer, moe_admin, super_admin */}
          <Route path="/admin/mora"                 element={<P><MoraSchoolsPage user={user} /></P>} />

          {/* Reports & analytics */}
          <Route path="/admin/reports"              element={<P><ReportsPage user={user} /></P>} />
          <Route path="/admin/moe-dashboard"        element={<P><MOEDashboardPage user={user} /></P>} />

          {/* EduBot — all roles */}
          <Route path="/admin/edubot"               element={<P><EduBotPage user={user} /></P>} />

          {/* Catch-all */}
          <Route path="*"                           element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <LangProvider>
        <App />
      </LangProvider>
    </React.StrictMode>,
  );
});
